import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'useGoalMappedCategories' });

interface MappedCategories {
  [goalId: string]: string[]; // Array of category names for each goal
}

export const useGoalMappedCategories = (focusId: string | undefined, rubricId: string | undefined) => {
  const [mappedCategories, setMappedCategories] = useState<MappedCategories>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!focusId || !rubricId) return;

    const fetchMappedCategories = async () => {
      setLoading(true);
      try {
        // First fetch all mappings for this focus
        const { data: mappings, error: mappingsError } = await supabase
          .from('goal_rubric_mappings')
          .select('goal_id, rubric_category')
          .eq('focus_id', focusId);

        if (mappingsError) {
          logger.error('Error fetching goal mappings:', mappingsError);
          return;
        }

        if (!mappings || mappings.length === 0) {
          setMappedCategories({});
          return;
        }

        // Get unique category IDs
        const categoryIds = [...new Set(mappings.map(m => m.rubric_category))];

        // Fetch category names
        const { data: categories, error: categoriesError } = await supabase
          .from('rubric_categories')
          .select('id, name')
          .in('id', categoryIds)
          .eq('rubric_id', rubricId);

        if (categoriesError) {
          logger.error('Error fetching category names:', categoriesError);
          return;
        }

        // Create a map of category ID to name
        const categoryMap = new Map(
          categories?.map(c => [c.id, c.name]) || []
        );

        // Group by goal ID and map to category names
        const result: MappedCategories = {};
        mappings.forEach(mapping => {
          if (!result[mapping.goal_id]) {
            result[mapping.goal_id] = [];
          }
          const categoryName = categoryMap.get(mapping.rubric_category);
          if (categoryName && !result[mapping.goal_id].includes(categoryName)) {
            result[mapping.goal_id].push(categoryName);
          }
        });

        // Sort category names for each goal
        Object.keys(result).forEach(goalId => {
          result[goalId].sort();
        });

        setMappedCategories(result);
      } catch (error) {
        logger.error('Error in fetchMappedCategories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMappedCategories();
  }, [focusId, rubricId]);

  return { mappedCategories, loading };
};